import { useId } from 'react'
import s from './DestinationTile.module.css'

/** The rail's four tones — the design system's `settingsTile[data-tone]` names. */
export type DestinationTone = 'day' | 'reach' | 'business' | 'plan'

/**
 * Luminance-to-tone duotones, in sRGB: the clay's ink body lands on the tone's
 * deep shade and its cream detail stays near-white, so the mark keeps its
 * relief on a pale wash of the same tone. Identical to the design system's
 * `#settings-icon-{day,reach,business,plan}` filters (the page's shared SVG
 * defs, drawn in its #sidebar section).
 */
const PIGMENTS: Record<DestinationTone, string> = {
  day: '0.113387 0.381440 0.038507 0 0.466667 0.183420 0.617035 0.062290 0 0.137255 0.197593 0.664715 0.067104 0 0.070588 0 0 0 1 0',
  reach: '0.207598 0.698372 0.070501 0 0.023529 0.147569 0.496433 0.050115 0 0.305882 0.151738 0.510456 0.051531 0 0.286275 0 0 0 1 0',
  business: '0.179251 0.603012 0.060875 0 0.156863 0.149237 0.502042 0.050682 0 0.298039 0.087541 0.294494 0.029729 0 0.588235 0 0 0 1 0',
  plan: '0.111719 0.375831 0.037940 0 0.474510 0.150904 0.507652 0.051248 0 0.290196 0.212600 0.715200 0.072200 0 0 0 0 0 1 0',
}

/**
 * A filled clay destination mark pigmented into its group's tone on a 26px
 * tinted tile — the settings rail's tile, and since the 2026-09-23 makeover the
 * main rail's too. `src` must be ink-on-transparent light art (the pigment maps
 * luminance), so dark theme keeps the same light tile and mark, as the design
 * system does. Decorative: the row's label is the accessible name.
 */
export function DestinationTile({
  src,
  tone,
  className,
}: {
  src: string
  tone: DestinationTone
  className?: string
}) {
  const id = useId()
  return (
    <span
      className={[s.tile, className].filter(Boolean).join(' ')}
      data-tone={tone}
      aria-hidden="true"
    >
      <svg viewBox="0 0 100 100" width="22" height="22" focusable="false">
        <defs>
          <filter id={id} colorInterpolationFilters="sRGB">
            <feColorMatrix type="matrix" values={PIGMENTS[tone]} />
          </filter>
        </defs>
        <image href={src} width="100" height="100" filter={`url(#${id})`} />
      </svg>
    </span>
  )
}
